/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * The automation canvas's step list, and how it maps onto what the engine
 * runs today (backend automation_service.execute_automation): one condition
 * gate, then the actions in order, where an action with a time delay is
 * scheduled that long after the trigger.
 *
 * On the canvas a Wait is a step of its own. An action's saved delay is the
 * sum of the waits above it, so "Wait 2 days, then Create task" is saved as a
 * task with a two-day delay and runs as drawn. Loading goes the other way:
 * actions are ordered by delay (stable, so the engine does exactly what it
 * did before) and each gap becomes a Wait.
 *
 * Steps: { id, kind: "action", action } | { id, kind: "wait", minutes, businessDays }
 *      | { id, kind: "condition", groups, groupLogic, yes: [steps], no: [steps] }.
 *
 * A flow with a condition step or a business-days wait cannot be flat actions;
 * it is saved as the engine's `flow` (backend Services/automation_flow.py),
 * where waits pause the run and conditions branch it. A list inside the tree
 * is addressed by a path: [] is the top list, ["c1", "yes"] the Yes branch of
 * step c1, ["c1", "yes", "c2", "no"] a branch inside that.
 */

let seq = 0;
export const newStepId = (prefix = "s") => `${prefix}${Date.now().toString(36)}${(seq += 1)}`;

export function delayMinutes(timeDelay) {
  if (!timeDelay?.enabled) return 0;
  const n = (v) => Math.max(0, parseInt(v, 10) || 0);
  return n(timeDelay.days) * 1440 + n(timeDelay.hours) * 60 + n(timeDelay.minutes);
}

export function minutesToDelay(minutes) {
  const m = Math.max(0, Math.round(minutes || 0));
  if (!m) return { enabled: false, days: 0, hours: 0, minutes: 0 };
  return { enabled: true, days: Math.floor(m / 1440), hours: Math.floor((m % 1440) / 60), minutes: m % 60 };
}

/** "2 days 3 h", "45 min". */
export function describeMinutes(minutes) {
  const d = minutesToDelay(minutes);
  if (!d.enabled) return "No delay";
  const parts = [];
  if (d.days) parts.push(`${d.days} day${d.days === 1 ? "" : "s"}`);
  if (d.hours) parts.push(`${d.hours} h`);
  if (d.minutes) parts.push(`${d.minutes} min`);
  return parts.join(" ");
}

/** Saved actions -> canvas steps (waits drawn between actions). */
export function actionsToSteps(actions = []) {
  const ordered = actions
    .map((action, i) => ({ action, i, delay: delayMinutes(action?.timeDelay) }))
    .sort((a, b) => a.delay - b.delay || a.i - b.i);
  const steps = [];
  let previous = 0;
  ordered.forEach(({ action, delay }) => {
    if (delay > previous) steps.push({ id: newStepId("w"), kind: "wait", minutes: delay - previous });
    previous = delay;
    steps.push({ id: newStepId("a"), kind: "action", action });
  });
  return steps;
}

/** Canvas steps -> the actions the engine saves, with cumulative delays. */
export function stepsToActions(steps = []) {
  let elapsed = 0;
  const actions = [];
  steps.forEach((step) => {
    if (step.kind === "wait") {
      elapsed += Math.max(0, step.minutes || 0);
      return;
    }
    actions.push({ ...step.action, timeDelay: minutesToDelay(elapsed) });
  });
  return actions;
}

export function insertStep(steps, index, step) {
  const next = [...steps];
  next.splice(Math.max(0, Math.min(index, next.length)), 0, step);
  return next;
}

export function moveStep(steps, from, to) {
  if (from === to || from < 0 || from >= steps.length) return steps;
  const next = [...steps];
  const [item] = next.splice(from, 1);
  // Dropping below itself: the gap it left shifts the target up by one.
  next.splice(to > from ? to - 1 : to, 0, item);
  return next;
}

export const removeStep = (steps, id) => steps.filter((s) => s.id !== id);

export const updateStep = (steps, id, patch) => steps.map((s) => (s.id === id ? { ...s, ...patch } : s));

export function newActionStep(type) {
  return {
    id: newStepId("a"),
    kind: "action",
    action: { type, parameters: {}, timeDelay: { enabled: false, days: 0, hours: 0, minutes: 0 } },
  };
}

export const newWaitStep = (minutes = 1440) => ({ id: newStepId("w"), kind: "wait", minutes });

/** A wait with no action after it delays nothing; the canvas says so. */
export function trailingWait(steps) {
  for (let i = steps.length - 1; i >= 0; i -= 1) {
    if (steps[i].kind === "action") return false;
    if (steps[i].kind === "wait") return true;
  }
  return false;
}

/** "When a lead is created". */
export function describeTrigger(object, event) {
  const noun = String(object || "record").replace(/_/g, " ").replace(/s$/, "");
  const e = String(event || "").toLowerCase();
  if (e.includes("schedule")) return object ? `On a schedule, for each matching ${noun}` : "On a schedule";
  const verb = e.includes("create") ? "created" : e.includes("update") ? "updated" : e.includes("delete") ? "deleted" : null;
  const article = /^[aeiou]/i.test(noun) ? "an" : "a";
  return verb ? `When ${article} ${noun} is ${verb}` : "Choose what starts this automation";
}

const short = (v, n = 60) => {
  const s = String(v ?? "").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
  return s.length > n ? `${s.slice(0, n - 1)}…` : s;
};

/** One line under an action card's title. */
export function describeAction(action) {
  const p = action?.parameters || {};
  switch (action?.type) {
    case "Update Field":
      return p.field ? `${p.field} → ${short(p.value, 40) || "(empty)"}` : "Choose a field to update";
    case "Create Task":
      return p.subject ? short(p.subject) : "Set the task details";
    case "Send Email":
      return p.subject || p.subjectOverride ? short(p.subjectOverride || p.subject) : p.template ? `Template ${short(p.template, 40)}` : "Choose a template or write the email";
    case "Create Record":
      return p.recordType ? `New ${String(p.recordType).replace(/_/g, " ")}` : "Choose the record to create";
    case "Update Related Record":
      return p.relatedRecordType ? `${String(p.relatedRecordType).replace(/_/g, " ")}${p.relatedField ? ` · ${p.relatedField}` : ""}` : "Choose the related record";
    case "Webhook":
      return p.url ? short(p.url) : p.webhookId ? "Configured webhook" : "Choose a webhook";
    default:
      return "Choose what this step does";
  }
}

/** "2 conditions · all must match" for the gate card. */
export function describeConditions(groups = [], groupLogic = "AND") {
  const count = groups.reduce((n, g) => n + (g.conditions || []).length, 0);
  if (!count) return { title: "Runs every time", sub: "No conditions" };
  const all = groups.length > 1 ? groupLogic === "AND" : (groups[0]?.logic || "AND") === "AND";
  return { title: count === 1 ? "Only when 1 condition matches" : `Only when ${count} conditions match`, sub: `${count} condition${count === 1 ? "" : "s"} · ${all ? "all" : "any"} must match` };
}

// ------------------------------------------------------------- the tree

export const MAX_STEPS = 40;
export const MAX_DEPTH = 3;

export const newConditionStep = () => ({ id: newStepId("c"), kind: "condition", groups: [], groupLogic: "AND", yes: [], no: [] });

/** Every step, depth first, in the order they are drawn. */
export function allSteps(steps = []) {
  const out = [];
  steps.forEach((s) => {
    out.push(s);
    if (s.kind === "condition") out.push(...allSteps(s.yes), ...allSteps(s.no));
  });
  return out;
}

export const allActions = (steps) => allSteps(steps).filter((s) => s.kind === "action");

/** Flat actions can hold it: actions and plain waits only. */
export const isLinear = (steps = []) => steps.every((s) => s.kind === "action" || (s.kind === "wait" && !s.businessDays));

export function depthOf(steps = [], level = 1) {
  return steps.reduce((d, s) => (s.kind === "condition" ? Math.max(d, depthOf(s.yes, level + 1), depthOf(s.no, level + 1)) : d), steps.length ? level : level - 1);
}

export function getList(steps, path = []) {
  let list = steps;
  for (let i = 0; i < path.length; i += 2) {
    const node = list.find((s) => s.id === path[i]);
    if (!node || node.kind !== "condition") return null;
    list = node[path[i + 1]] || [];
  }
  return list;
}

function mapList(steps, path, fn) {
  if (!path.length) return fn(steps);
  const [id, branch, ...rest] = path;
  return steps.map((s) => (s.id === id && s.kind === "condition" ? { ...s, [branch]: mapList(s[branch] || [], rest, fn) } : s));
}

export const insertAt = (steps, path, index, step) => mapList(steps, path, (list) => insertStep(list, index ?? list.length, step));

/** { step, path, index } for a step anywhere in the tree, or null. */
export function findStep(steps, id, path = []) {
  for (let i = 0; i < steps.length; i += 1) {
    const s = steps[i];
    if (s.id === id) return { step: s, path, index: i };
    if (s.kind === "condition") {
      const hit = findStep(s.yes || [], id, [...path, s.id, "yes"]) || findStep(s.no || [], id, [...path, s.id, "no"]);
      if (hit) return hit;
    }
  }
  return null;
}

export function removeDeep(steps, id) {
  return steps
    .filter((s) => s.id !== id)
    .map((s) => (s.kind === "condition" ? { ...s, yes: removeDeep(s.yes || [], id), no: removeDeep(s.no || [], id) } : s));
}

export function updateDeep(steps, id, patch) {
  return steps.map((s) => {
    if (s.id === id) return { ...s, ...patch };
    if (s.kind === "condition") return { ...s, yes: updateDeep(s.yes || [], id, patch), no: updateDeep(s.no || [], id, patch) };
    return s;
  });
}

/** Move a step (with its branches) to `index` of the list at `toPath`. */
export function moveDeep(steps, id, toPath, toIndex) {
  const from = findStep(steps, id);
  if (!from) return steps;
  if (toPath.includes(id)) return steps; // into its own branch
  const samePlace = from.path.join("/") === toPath.join("/");
  if (samePlace && (toIndex === from.index || toIndex === from.index + 1)) return steps;
  const index = samePlace && toIndex > from.index ? toIndex - 1 : toIndex;
  return insertAt(removeDeep(steps, id), toPath, index, from.step);
}

/** A copy with new ids throughout, for Duplicate. */
export function cloneStep(step) {
  const copy = JSON.parse(JSON.stringify(step));
  const renew = (s) => ({
    ...s,
    id: newStepId(s.kind === "wait" ? "w" : s.kind === "condition" ? "c" : "a"),
    ...(s.kind === "condition" ? { yes: (s.yes || []).map(renew), no: (s.no || []).map(renew) } : {}),
  });
  return renew(copy);
}

/** Canvas steps -> the engine's flow. */
export function stepsToFlow(steps = []) {
  const convert = (list) =>
    list.map((s) => {
      if (s.kind === "wait") return { id: s.id, kind: "wait", minutes: Math.max(1, s.minutes || 0), business_days: Boolean(s.businessDays) };
      if (s.kind === "condition") return { id: s.id, kind: "condition", groups: s.groups || [], group_logic: s.groupLogic || "AND", yes: convert(s.yes || []), no: convert(s.no || []) };
      return { id: s.id, kind: "action", type: s.action?.type, parameters: s.action?.parameters || {} };
    });
  return { steps: convert(steps) };
}

/** The engine's flow -> canvas steps. */
export function flowToSteps(flow) {
  const convert = (list = []) =>
    list.map((s) => {
      if (s.kind === "wait") return { id: s.id, kind: "wait", minutes: s.minutes, businessDays: Boolean(s.business_days) };
      if (s.kind === "condition") return { id: s.id, kind: "condition", groups: s.groups || [], groupLogic: s.group_logic || "AND", yes: convert(s.yes), no: convert(s.no) };
      return { id: s.id, kind: "action", action: { type: s.type, parameters: s.parameters || {}, timeDelay: { enabled: false, days: 0, hours: 0, minutes: 0 } } };
    });
  return convert(flow?.steps);
}

/** What to save: flat actions for a linear flow, otherwise the flow too. */
export function savePayload(steps) {
  if (isLinear(steps)) return { actions: stepsToActions(steps), flow: null };
  return { actions: allActions(steps).map((s) => ({ ...s.action, timeDelay: minutesToDelay(0) })), flow: stepsToFlow(steps) };
}

/** The tree with every branch condition's groups passed through `fn`. */
export function mapConditionGroups(steps = [], fn) {
  return steps.map((s) =>
    s.kind === "condition"
      ? { ...s, groups: fn(s.groups || []), yes: mapConditionGroups(s.yes, fn), no: mapConditionGroups(s.no, fn) }
      : s
  );
}

/** True when a condition sits where its branches would pass the depth limit. */
export function tooDeep(steps = [], level = 1) {
  return steps.some((s) => s.kind === "condition" && (level >= MAX_DEPTH || tooDeep(s.yes, level + 1) || tooDeep(s.no, level + 1)));
}
